/**
 * Formateo de presentación.
 *
 * Vive en un módulo aparte y no en el servicio de API a propósito: el backend
 * manda los precios como string decimal para no perder precisión al serializar
 * (`"0.10"` no es `0.1` en IEEE-754), y el formateo a moneda es una decisión de
 * la vista. Si se hiciera en el servicio, el valor original se perdería para
 * cuando la cotización recalcula totales.
 */

/**
 * Pesos colombianos.
 *
 * El locale es `es-CO` y no `es-ES` por dos razones, y las dos importan:
 *
 * - Con `es-ES` y `currency: 'COP'`, `Intl` escribe el **nombre** de la moneda en
 *   vez de su símbolo. El símbolo lo pone el locale, no el código: cambiar solo
 *   `currency` habría sacado «1200 COP» en pantalla.
 * - `es-CO` pone el símbolo **delante** y el `es-ES` detrás. En Colombia el peso
 *   va delante, así que el locale no es un detalle de formato.
 *
 * Los decimales se fuerzan a **cero**, y no por defecto. `Intl` ya redondea a
 * entero cuando la moneda no tiene unidad fraccionaria en circulación —que es el
 * caso del peso colombiano—, así que no hacer nada daría lo mismo. Se pone
 * explícito para que el valor no dependa de una suposición sobre el locale: si
 * alguien cambiara el `currency`, los ceros seguirían marcados y el cambio se
 * vería en el formato en lugar de estar escondido en el comportamiento.
 *
 * No es una pérdida de precisión: el backend no acepta importes con decimales
 * (`integer` en la validación) y las columnas de la base son enteras, así que
 * ningún dato llega aquí con centavos que se estén ocultando. Los precios, las
 * cantidades y los totales son números enteros de una vez.
 *
 * El separador de millares y el decimal no cambian: `es-CO` usa punto y coma igual
 * que `es-ES`, así que lo que ya se veía sigue viéndose y solo cambia el símbolo.
 *
 * Ojo con una diferencia real: `es-ES` no ponía separador de millares en cuatro
 * cifras (`1200 €`) y `es-CO` sí (`$ 1.200`). No es un error de formato, es el
 * estándar de cada locale.
 */
const CURRENCY_FORMAT = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/**
 * Formatea un decimal string del backend como importe en pesos colombianos.
 *
 * Un valor no numérico devuelve un guion en lugar de `NaN` o `Infinity`: la
 * celda debe quedar legible aunque el dato venga mal, y un `NaN` en pantalla
 * parece un error de la aplicación.
 */
export function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'

  const parsed = typeof value === 'number' ? value : Number.parseFloat(value)
  if (!Number.isFinite(parsed)) return '—'

  return CURRENCY_FORMAT.format(parsed)
}

/**
 * Convierte un valor de fecha en algo que `new Date` entienda sin sorpresas de zona.
 *
 * Una fecha suelta como `"2026-10-01"` **no** lleva zona horaria, y `new Date` la
 * interpreta como medianoche UTC. Al formatearla en hora local, en cualquier zona
 * por detrás de UTC sale el día anterior: el 1 de octubre se ve el 30 de
 * septiembre. En un CRM eso es una fecha equivocada, y hay quien acaba tomando
 * decisiones de facturación a partir de ella.
 *
 * Por eso, cuando el valor es solo una fecha, se monta a mano con la hora local.
 * Cuando trae hora —un `created_at` con timestamp— se respeta la que trae.
 */
function toLocalDate(value: string): Date | null {
  const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (soloFecha !== null) {
    return new Date(Number(soloFecha[1]), Number(soloFecha[2]) - 1, Number(soloFecha[3]))
  }

  const conFecha = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value)
  if (conFecha !== null) {
    return new Date(
      Number(conFecha[1]),
      Number(conFecha[2]) - 1,
      Number(conFecha[3]),
      Number(conFecha[4]),
      Number(conFecha[5]),
    )
  }

  const fallback = new Date(value)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

/**
 * Formatea una fecha como `dd/mm/aaaa`.
 *
 * Se construye a mano y no con `Intl` porque `dateStyle: 'short'` en `es-ES` da
 * un año de dos cifras (`1/10/26`) y `dateStyle: 'medium'` mete letras
 * (`1 oct 2026`). Ninguno de los dos es el formato que se pide, que es día, mes y
 * año de cuatro cifras.
 */
export function formatShortDate(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'

  const parsed = toLocalDate(value)
  if (parsed === null) return '—'

  const day = String(parsed.getDate()).padStart(2, '0')
  const month = String(parsed.getMonth() + 1).padStart(2, '0')

  return `${day}/${month}/${parsed.getFullYear()}`
}

/**
 * Convierte `YYYY-MM-DD` en una `Date` **local** para el selector de calendario.
 *
 * Un `new Date('2026-10-01')` es la medianoche **en UTC**, y al pintarlo al oeste de
 * UTC sale el día anterior. El selector de fechas guarda la fecha en la zona del
 * navegador, así que el cambio se hace por partes y a la hora local.
 *
 * @param value Fecha en `YYYY-MM-DD`, o vacío si no hay ninguna.
 */
export function toLocalDateInput(value: string | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null

  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)

  if (partes === null) {
    const fallback = toLocalDate(value)
    return fallback
  }

  const [, anio, mes, dia] = partes

  return new Date(Number(anio), Number(mes) - 1, Number(dia))
}

/**
 * Convierte lo que emite el selector de calendario en `YYYY-MM-DD`.
 *
 * El formato sale de las partes en local, no de `toISOString()`, que devuelve UTC y
 * corría el día hacia atrás por la misma razón que `toLocalDateInput` deshace.
 *
 * @param value La `Date` del selector, o `null` si el campo está vacío.
 */
export function fromLocalDateInput(value: Date | null | undefined): string {
  if (value === null || value === undefined) return ''

  if (Number.isNaN(value.getTime())) return ''

  const dia = String(value.getDate()).padStart(2, '0')
  const mes = String(value.getMonth() + 1).padStart(2, '0')

  return `${value.getFullYear()}-${mes}-${dia}`
}

/** Formatea una fecha ISO o `null` como fecha corta en locale español. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'

  const parsed = toLocalDate(value)
  if (parsed === null) return '—'

  return new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' }).format(parsed)
}
